import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TextInput } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { MEDIA_QUERIES } from '../../../utils/responsive';
import {
  ROLLING_RECEIVED_DATE_PRESETS,
  receivedDatePresetLabel,
  type ReceivedDatePreset,
} from '../utils/receivedDatePresets';
import {
  EMAIL_LIST_SORT_OPTIONS,
  emailListSortLabel,
  type EmailListSortBy,
} from '../utils/emailListSortStorage';
import EmailListLayoutToggle from './EmailListLayoutToggle';
import {
  QuickFilterIcon,
  SearchInFieldIcon,
  type QuickFilterKey,
  type SearchInFieldKey,
} from '../utils/emailFilterIcons';
import {
  DEFAULT_EMAIL_SEARCH_IN_FIELDS,
  defaultEmailSearchInSet,
  isDefaultEmailSearchIn,
} from '../utils/emailSearchDefaults';

/** Shared height for all controls in the email list filter bar */
const FILTER_BAR_CONTROL_H = 'h-8 min-h-8';

interface FilterMenuOptionProps {
  isActive: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  borderClass: string;
  textClass: string;
  hoverClass: string;
}

function FilterMenuOption({
  isActive,
  onClick,
  label,
  icon,
  borderClass,
  textClass,
  hoverClass,
}: FilterMenuOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full h-8 rounded-md px-2 flex items-center gap-2 text-left text-xs transition-colors',
        isActive ? 'bg-indigo-600 text-white' : cn(textClass, hoverClass),
      )}
    >
      <span className="inline-flex items-center justify-center w-4 h-4 shrink-0">{icon}</span>
      <span className="flex-1">{label}</span>
      <span
        className={cn(
          'inline-flex items-center justify-center w-3.5 h-3.5 rounded border shrink-0',
          isActive ? 'border-white/80' : borderClass,
        )}
      >
        {isActive && (
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
    </button>
  );
}

interface EmailFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onQuickFilter: (filter: string, active: boolean) => void;
  activeQuickFilters: Set<string>;
  searchInFields: Set<'sender' | 'recipients' | 'subject' | 'body' | 'attachments'>;
  onSearchInFieldsChange: (fields: Set<'sender' | 'recipients' | 'subject' | 'body' | 'attachments'>) => void;
  resultCount?: number;
  /** When set, search folder is active in background; filters apply as extra */
  searchFolderName?: string | null;
  /** Compact period from search folder config (e.g. "Last 30 days", "2025") */
  searchFolderPeriodLabel?: string | null;
  showYearFilter?: boolean;
  selectedYear?: number | null;
  onYearChange?: (year: number | null) => void;
  partitionPreset?: ReceivedDatePreset | null;
  onPartitionPresetChange?: (preset: ReceivedDatePreset | null) => void;
  /** Show flat / thread segmented control (hidden for drafts/templates) */
  showListLayoutToggle?: boolean;
  threadedView?: boolean;
  onThreadedViewChange?: (threadedView: boolean) => void;
  showSortControl?: boolean;
  listSortBy?: EmailListSortBy;
  onListSortByChange?: (sortBy: EmailListSortBy) => void;
  /** Hide the Archived quick filter when already viewing the Archived sidebar folder */
  hideArchivedQuickFilter?: boolean;
}

const EmailFilters: React.FC<EmailFiltersProps> = ({
  searchTerm,
  onSearchChange,
  onQuickFilter,
  activeQuickFilters,
  searchInFields,
  onSearchInFieldsChange,
  resultCount,
  searchFolderName,
  searchFolderPeriodLabel,
  showYearFilter = false,
  selectedYear = null,
  onYearChange,
  partitionPreset = null,
  onPartitionPresetChange,
  showListLayoutToggle = false,
  threadedView = false,
  onThreadedViewChange,
  showSortControl = false,
  listSortBy = 'date',
  onListSortByChange,
  hideArchivedQuickFilter = false,
}) => {
  const { colors } = useTheme();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [quickFiltersOpen, setQuickFiltersOpen] = useState(false);
  const quickFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const quickFiltersDropdownRef = useRef<HTMLDivElement>(null);
  const [quickFiltersRect, setQuickFiltersRect] = useState<{ top: number; left: number } | null>(null);
  const [searchInOpen, setSearchInOpen] = useState(false);
  const searchInButtonRef = useRef<HTMLButtonElement>(null);
  const searchInDropdownRef = useRef<HTMLDivElement>(null);
  const [searchInRect, setSearchInRect] = useState<{ top: number; left: number } | null>(null);
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const dateMenuButtonRef = useRef<HTMLButtonElement>(null);
  const dateMenuDropdownRef = useRef<HTMLDivElement>(null);
  const [dateMenuRect, setDateMenuRect] = useState<{ top: number; left: number } | null>(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const moreFiltersButtonRef = useRef<HTMLButtonElement>(null);
  const moreFiltersDropdownRef = useRef<HTMLDivElement>(null);
  const [moreFiltersRect, setMoreFiltersRect] = useState<{ top: number; left: number } | null>(null);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuButtonRef = useRef<HTMLButtonElement>(null);
  const sortMenuDropdownRef = useRef<HTMLDivElement>(null);
  const [sortMenuRect, setSortMenuRect] = useState<{ top: number; left: number } | null>(null);
  const isCompactFilters = useMediaQuery(MEDIA_QUERIES.mobile);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        onSearchChange(localSearchTerm);
      }
    }, 280);
    return () => window.clearTimeout(handle);
  }, [localSearchTerm, searchTerm, onSearchChange]);

  useEffect(() => {
    if (!quickFiltersOpen) return;
    const updateRect = () => {
      if (!quickFiltersButtonRef.current) return;
      const r = quickFiltersButtonRef.current.getBoundingClientRect();
      setQuickFiltersRect({ top: r.bottom + 6, left: r.left });
    };
    updateRect();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (quickFiltersButtonRef.current?.contains(target)) return;
      if (quickFiltersDropdownRef.current?.contains(target)) return;
      setQuickFiltersOpen(false);
    };
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [quickFiltersOpen]);

  useEffect(() => {
    if (!searchInOpen) return;
    const updateRect = () => {
      if (!searchInButtonRef.current) return;
      const r = searchInButtonRef.current.getBoundingClientRect();
      setSearchInRect({ top: r.bottom + 6, left: r.left });
    };
    updateRect();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (searchInButtonRef.current?.contains(target)) return;
      if (searchInDropdownRef.current?.contains(target)) return;
      setSearchInOpen(false);
    };
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [searchInOpen]);

  useEffect(() => {
    if (!dateMenuOpen) return;
    const updateRect = () => {
      if (!dateMenuButtonRef.current) return;
      const r = dateMenuButtonRef.current.getBoundingClientRect();
      const panelW = 288;
      let left = r.left;
      if (left + panelW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - 8 - panelW);
      }
      setDateMenuRect({ top: r.bottom + 6, left });
    };
    updateRect();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dateMenuButtonRef.current?.contains(target)) return;
      if (dateMenuDropdownRef.current?.contains(target)) return;
      setDateMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDateMenuOpen(false);
    };
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [dateMenuOpen]);

  useEffect(() => {
    if (!moreFiltersOpen) return;
    const updateRect = () => {
      if (!moreFiltersButtonRef.current) return;
      const r = moreFiltersButtonRef.current.getBoundingClientRect();
      const panelW = 288;
      let left = r.left;
      if (left + panelW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - 8 - panelW);
      }
      setMoreFiltersRect({ top: r.bottom + 6, left });
    };
    updateRect();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (moreFiltersButtonRef.current?.contains(target)) return;
      if (moreFiltersDropdownRef.current?.contains(target)) return;
      setMoreFiltersOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreFiltersOpen(false);
    };
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreFiltersOpen]);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const updateRect = () => {
      if (!sortMenuButtonRef.current) return;
      const r = sortMenuButtonRef.current.getBoundingClientRect();
      const panelW = 240;
      let left = r.left;
      if (left + panelW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - 8 - panelW);
      }
      setSortMenuRect({ top: r.bottom + 6, left });
    };
    updateRect();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (sortMenuButtonRef.current?.contains(target)) return;
      if (sortMenuDropdownRef.current?.contains(target)) return;
      setSortMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSortMenuOpen(false);
    };
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [sortMenuOpen]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(localSearchTerm);
  };

  const handleSearchInChange = (value: string | number | Array<string | number> | null) => {
    const arr = Array.isArray(value) ? value : value != null ? [value] : [];
    const normalized = arr.filter((v): v is 'sender' | 'recipients' | 'subject' | 'body' | 'attachments' =>
      v === 'sender' || v === 'recipients' || v === 'subject' || v === 'body' || v === 'attachments',
    );
    if (normalized.length === 0) {
      onSearchInFieldsChange(defaultEmailSearchInSet());
      return;
    }
    onSearchInFieldsChange(new Set<'sender' | 'recipients' | 'subject' | 'body' | 'attachments'>(normalized));
  };

  const quickFilters: ReadonlyArray<{ key: QuickFilterKey; label: string }> = [
    { key: 'unread', label: 'Unread' },
    { key: 'starred', label: 'Starred' },
    { key: 'assignedToMe', label: 'Assigned to me' },
    { key: 'statusOpen', label: 'Open' },
    { key: 'statusWorking', label: 'In Progress' },
    { key: 'statusClosed', label: 'Closed' },
    { key: 'attachments', label: 'Attachments' },
    { key: 'linked', label: 'Linked' },
    ...(hideArchivedQuickFilter ? [] : [{ key: 'archived' as const, label: 'Archived' }]),
  ] as const;
  const activeQuickCount = quickFilters.filter((f) => activeQuickFilters.has(f.key)).length;
  const searchInNonDefault = !isDefaultEmailSearchIn(searchInFields);
  const filtersActiveCount = activeQuickCount + (searchInNonDefault ? 1 : 0);

  const searchInOptions: ReadonlyArray<{ key: SearchInFieldKey; label: string }> = [
    { key: 'sender', label: 'Sender' },
    { key: 'recipients', label: 'Recipients' },
    { key: 'subject', label: 'Subject' },
    { key: 'body', label: 'Body' },
    { key: 'attachments', label: 'Attachments' },
  ] as const;
  const currentYear = new Date().getUTCFullYear();
  const hasPartitionFilter = partitionPreset != null || selectedYear != null;
  const yearStepEnabled = partitionPreset == null;
  const presetOptions = ROLLING_RECEIVED_DATE_PRESETS.map((p) => ({
    id: p.id,
    label: p.label,
    hint: 'By received date',
  }));
  const yearOptions = Array.from({ length: 16 }, (_, i) => currentYear - i);
  const dateTriggerLabel =
    partitionPreset != null
      ? receivedDatePresetLabel(partitionPreset)
      : selectedYear != null
        ? String(selectedYear)
        : 'Any time';

  return (
    <div className={cn('flex-shrink-0 border-b', colors.surface, colors.border)}>
      <div
        className={cn(
          'px-3 py-2 flex flex-nowrap items-center gap-2 min-w-0 overflow-x-auto',
        )}
      >
      {showYearFilter && (
        <div
          className={cn(
            'inline-flex h-8 flex-shrink-0 items-stretch overflow-hidden rounded-md border box-border',
            colors.border,
            colors.surface,
          )}
        >
          <button
            type="button"
            disabled={!yearStepEnabled}
            onClick={() => {
              if (!onYearChange || !yearStepEnabled) return;
              const base = selectedYear ?? currentYear;
              onYearChange(base - 1);
            }}
            className={cn(
              'h-full w-7 shrink-0 inline-flex items-center justify-center border-r transition-colors',
              colors.border,
              colors.surface,
              yearStepEnabled ? cn(colors.interactiveHover, colors.text) : 'opacity-40 cursor-not-allowed',
            )}
            aria-label="Previous year"
            title="Previous year"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="relative flex h-full min-w-0 flex-shrink-0 self-stretch">
            <button
              ref={dateMenuButtonRef}
              type="button"
              onClick={() => {
                setQuickFiltersOpen(false);
                setSearchInOpen(false);
                setDateMenuOpen((v: boolean) => !v);
              }}
              className={cn(
                'h-full min-w-[108px] max-w-[148px] pl-2 pr-1.5 inline-flex items-center gap-1.5 border-r text-left text-xs font-medium transition-colors',
                colors.border,
                colors.surface,
                colors.text,
                hasPartitionFilter && 'text-indigo-700 dark:text-indigo-300',
                dateMenuOpen && 'bg-indigo-50 dark:bg-indigo-950/40',
                colors.interactiveHover,
              )}
              aria-haspopup="menu"
              aria-expanded={dateMenuOpen}
              aria-label="Filter by date or year"
            >
              <svg className={cn('w-3.5 h-3.5 flex-shrink-0', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="flex-1 min-w-0 truncate">{dateTriggerLabel}</span>
              <svg className={cn('w-3 h-3 flex-shrink-0 opacity-60', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {dateMenuOpen && dateMenuRect && createPortal(
              <div
                ref={dateMenuDropdownRef}
                className={cn(
                  'fixed w-72 rounded-xl border shadow-xl overflow-hidden flex flex-col',
                  colors.surface,
                  colors.border
                )}
                style={{ top: dateMenuRect.top, left: dateMenuRect.left, ...zStyle(Z_INDEX.portaledDropdown) }}
                role="menu"
                aria-label="Date and year"
              >
                <div className={cn('px-3 py-2 border-b', colors.border)}>
                  <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>Time range</p>
                  <p className={cn('text-[10px] mt-0.5', colors.textMuted)}>Recent options use when the message was received.</p>
                </div>
                <div className="p-2 max-h-[min(20rem,calc(100vh-6rem))] overflow-y-auto">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={!hasPartitionFilter}
                    onClick={() => {
                      onYearChange?.(null);
                      onPartitionPresetChange?.(null);
                      setDateMenuOpen(false);
                    }}
                    className={cn(
                      'w-full rounded-lg px-2.5 py-2 flex items-center gap-2 text-left text-xs transition-colors mb-1',
                      !hasPartitionFilter ? 'bg-indigo-600 text-white' : cn(colors.text, colors.interactiveHover),
                    )}
                  >
                    <span className="inline-flex items-center justify-center w-4 h-4">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <span className="flex-1">
                      <span className="block font-medium">Any time</span>
                      <span className={cn('block text-[10px] mt-0.5', !hasPartitionFilter ? 'text-white/80' : colors.textMuted)}>
                        No date filter
                      </span>
                    </span>
                    <span className={cn('inline-flex items-center justify-center w-3.5 h-3.5 rounded border', !hasPartitionFilter ? 'border-white/80' : colors.border)}>
                      {!hasPartitionFilter && (
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                  </button>

                  <p className={cn('px-1 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>Recent</p>
                  <div className="space-y-0.5 mb-2">
                    {presetOptions.map((opt) => {
                      const active = partitionPreset === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={active}
                          onClick={() => {
                            onPartitionPresetChange?.(opt.id);
                            setDateMenuOpen(false);
                          }}
                          className={cn(
                            'w-full rounded-lg px-2.5 py-2 flex items-center gap-2 text-left text-xs transition-colors',
                            active ? 'bg-indigo-600 text-white' : cn(colors.text, colors.interactiveHover),
                          )}
                        >
                          <span className="inline-flex items-center justify-center w-4 h-4">
                            <svg className={cn('w-4 h-4', active ? 'text-white' : 'text-sky-500')} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-medium">{opt.label}</span>
                            <span className={cn('block text-[10px] mt-0.5 truncate', active ? 'text-white/80' : colors.textMuted)}>
                              {opt.hint}
                            </span>
                          </span>
                          <span className={cn('inline-flex items-center justify-center w-3.5 h-3.5 rounded border flex-shrink-0', active ? 'border-white/80' : colors.border)}>
                            {active && (
                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <p className={cn('px-1 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>Calendar year</p>
                  <div className="grid grid-cols-4 gap-1">
                    {yearOptions.map((year) => {
                      const active = selectedYear === year && partitionPreset == null;
                      return (
                        <button
                          key={year}
                          type="button"
                          role="menuitemradio"
                          aria-checked={active}
                          onClick={() => {
                            onYearChange?.(year);
                            setDateMenuOpen(false);
                          }}
                          className={cn(
                            'h-8 rounded-md text-xs font-medium transition-colors inline-flex items-center justify-center gap-1',
                            active ? 'bg-indigo-600 text-white shadow-sm' : cn(colors.text, colors.interactiveHover, 'border', colors.border),
                          )}
                        >
                          <svg className={cn('w-3 h-3 shrink-0', active ? 'text-white' : colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {year}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>,
              document.body
            )}
          </div>

          <button
            type="button"
            disabled={!yearStepEnabled}
            onClick={() => {
              if (!onYearChange || !yearStepEnabled) return;
              const base = selectedYear ?? currentYear;
              onYearChange(base + 1);
            }}
            className={cn(
              'h-full w-7 shrink-0 inline-flex items-center justify-center transition-colors',
              colors.surface,
              hasPartitionFilter && 'border-r',
              hasPartitionFilter && colors.border,
              yearStepEnabled ? cn(colors.interactiveHover, colors.text) : 'opacity-40 cursor-not-allowed',
            )}
            aria-label="Next year"
            title="Next year"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {hasPartitionFilter && (
            <button
              type="button"
              onClick={() => {
                onYearChange?.(null);
                onPartitionPresetChange?.(null);
              }}
              className={cn(
                'h-full px-2 text-[10px] font-semibold uppercase tracking-wide shrink-0 inline-flex items-center transition-colors',
                colors.surface,
                colors.interactiveHover,
                'text-indigo-700 dark:text-indigo-300',
              )}
              title="Clear date filter"
            >
              Reset
            </button>
          )}
        </div>
      )}

      <form
        onSubmit={handleSearchSubmit}
        className="flex h-8 min-w-[140px] flex-1 basis-[220px] max-w-full items-center"
      >
        <div className={cn('relative h-8 flex-1 min-w-0 rounded-lg [&_input]:!min-h-8 [&_input]:h-8', colors.surface)}>
          <span className={cn('absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none', colors.textMuted)}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <TextInput
            value={localSearchTerm}
            onChange={(e) => {
              const next = e.target.value;
              setLocalSearchTerm(next);
              if (next === '') {
                onSearchChange('');
              }
            }}
            placeholder={
              searchFolderName
                ? searchFolderPeriodLabel
                  ? `Search in "${searchFolderName}" (${searchFolderPeriodLabel})`
                  : `Search in "${searchFolderName}"`
                : 'Search emails'
            }
            size="sm"
            className={cn(
              FILTER_BAR_CONTROL_H,
              '!min-h-8 pl-9 pr-8 w-full text-sm rounded-lg border shadow-sm py-1.5',
              colors.border,
              colors.surface
            )}
            showClearButton={true}
          />
        </div>
      </form>

      {isCompactFilters ? (
        <div className="relative flex-shrink-0">
          <button
            ref={moreFiltersButtonRef}
            type="button"
            onClick={() => {
              setDateMenuOpen(false);
              setMoreFiltersOpen((v) => !v);
            }}
            className={cn(
              FILTER_BAR_CONTROL_H,
              'px-2.5 rounded-md border cursor-pointer text-xs inline-flex items-center gap-1.5 select-none',
              colors.border,
              colors.text,
              colors.surface
            )}
            aria-haspopup="menu"
            aria-expanded={moreFiltersOpen}
            aria-label="Filters"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19l-4 2v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            Filters
            {filtersActiveCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-indigo-600 text-white text-[10px] px-1">
                {filtersActiveCount}
              </span>
            )}
          </button>
          {moreFiltersOpen && moreFiltersRect && createPortal(
            <div
              ref={moreFiltersDropdownRef}
              className={cn(
                'fixed w-72 rounded-xl border shadow-xl overflow-hidden flex flex-col max-h-[min(24rem,calc(100vh-6rem))]',
                colors.surface,
                colors.border
              )}
              style={{ top: moreFiltersRect.top, left: moreFiltersRect.left, ...zStyle(Z_INDEX.portaledDropdown) }}
              role="menu"
              aria-label="Email filters"
            >
              <div className={cn('px-3 py-2 border-b shrink-0', colors.border)}>
                <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>Quick filters</p>
              </div>
              <div className="p-2 overflow-y-auto min-h-0">
                <div className="space-y-1 mb-3">
                  {quickFilters.map((filter) => {
                    const isActive = activeQuickFilters.has(filter.key);
                    return (
                      <FilterMenuOption
                        key={filter.key}
                        isActive={isActive}
                        label={filter.label}
                        icon={<QuickFilterIcon filterKey={filter.key} active={isActive} />}
                        borderClass={colors.border}
                        textClass={colors.text}
                        hoverClass={colors.interactiveHover}
                        onClick={() => onQuickFilter(filter.key, !isActive)}
                      />
                    );
                  })}
                </div>
                <p className={cn('px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>Search in</p>
                <div className="space-y-1">
                  {searchInOptions.map((option) => {
                    const isActive = searchInFields.has(option.key);
                    return (
                      <FilterMenuOption
                        key={option.key}
                        isActive={isActive}
                        label={option.label}
                        icon={<SearchInFieldIcon fieldKey={option.key} active={isActive} />}
                        borderClass={colors.border}
                        textClass={colors.text}
                        hoverClass={colors.interactiveHover}
                        onClick={() => {
                          const next = new Set(searchInFields);
                          if (isActive) next.delete(option.key);
                          else next.add(option.key);
                          const arr = Array.from(next);
                          handleSearchInChange(arr.length ? arr : [...DEFAULT_EMAIL_SEARCH_IN_FIELDS]);
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      ) : (
      <>
      <div className="relative flex-shrink-0">
        <button
          ref={quickFiltersButtonRef}
          type="button"
          onClick={() => {
            setDateMenuOpen(false);
            setSearchInOpen(false);
            setQuickFiltersOpen((v) => !v);
          }}
          className={cn(
            FILTER_BAR_CONTROL_H,
            'px-2.5 rounded-md border cursor-pointer text-xs inline-flex items-center gap-1.5 select-none',
            colors.border,
            colors.text,
            colors.surface
          )}
          aria-haspopup="menu"
          aria-expanded={quickFiltersOpen}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19l-4 2v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          Quick filters
          {activeQuickCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-indigo-600 text-white text-[10px] px-1">
              {activeQuickCount}
            </span>
          )}
        </button>
        {quickFiltersOpen && quickFiltersRect && createPortal(
          <div
            ref={quickFiltersDropdownRef}
            className={cn(
              'fixed w-64 rounded-lg border shadow-lg p-2',
              colors.surface,
              colors.border
            )}
            style={{ top: quickFiltersRect.top, left: quickFiltersRect.left, ...zStyle(Z_INDEX.portaledDropdown) }}
            role="menu"
          >
            <div className="space-y-1">
              {quickFilters.map((filter) => {
                const isActive = activeQuickFilters.has(filter.key);
                return (
                  <FilterMenuOption
                    key={filter.key}
                    isActive={isActive}
                    label={filter.label}
                    icon={<QuickFilterIcon filterKey={filter.key} active={isActive} />}
                    borderClass={colors.border}
                    textClass={colors.text}
                    hoverClass={colors.interactiveHover}
                    onClick={() => onQuickFilter(filter.key, !isActive)}
                  />
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>

      <div className="relative flex-shrink-0">
        <button
          ref={searchInButtonRef}
          type="button"
          onClick={() => {
            setDateMenuOpen(false);
            setQuickFiltersOpen(false);
            setSearchInOpen((v) => !v);
          }}
          className={cn(
            FILTER_BAR_CONTROL_H,
            'px-2.5 rounded-md border text-xs inline-flex items-center gap-1.5',
            colors.border,
            colors.text,
            colors.surface
          )}
          aria-haspopup="menu"
          aria-expanded={searchInOpen}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6h10M4 6h2m-2 6h10m4 0h2M4 18h2m4 0h10" />
          </svg>
          Search in
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-indigo-600 text-white text-[10px] px-1">
            {searchInFields.size}
          </span>
        </button>
        {searchInOpen && searchInRect && createPortal(
          <div
            ref={searchInDropdownRef}
            className={cn(
              'fixed w-56 rounded-lg border shadow-lg p-2',
              colors.surface,
              colors.border
            )}
            style={{ top: searchInRect.top, left: searchInRect.left, ...zStyle(Z_INDEX.portaledDropdown) }}
            role="menu"
          >
            <div className="space-y-1">
              {searchInOptions.map((option) => {
                const isActive = searchInFields.has(option.key);
                return (
                  <FilterMenuOption
                    key={option.key}
                    isActive={isActive}
                    label={option.label}
                    icon={<SearchInFieldIcon fieldKey={option.key} active={isActive} />}
                    borderClass={colors.border}
                    textClass={colors.text}
                    hoverClass={colors.interactiveHover}
                    onClick={() => {
                      const next = new Set(searchInFields);
                      if (isActive) next.delete(option.key);
                      else next.add(option.key);
                      const arr = Array.from(next);
                      handleSearchInChange(arr.length ? arr : [...DEFAULT_EMAIL_SEARCH_IN_FIELDS]);
                    }}
                  />
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>
      </>
      )}

      <div className="ml-auto flex h-8 flex-shrink-0 items-center gap-2">
        {showSortControl && onListSortByChange && (
          <div className="relative flex-shrink-0">
            <button
              ref={sortMenuButtonRef}
              type="button"
              onClick={() => {
                setQuickFiltersOpen(false);
                setSearchInOpen(false);
                setDateMenuOpen(false);
                setMoreFiltersOpen(false);
                setSortMenuOpen((v) => !v);
              }}
              className={cn(
                FILTER_BAR_CONTROL_H,
                'px-2.5 rounded-md border text-xs inline-flex items-center gap-1.5 max-w-[168px]',
                colors.border,
                colors.text,
                colors.surface,
                listSortBy !== 'date' && 'border-indigo-300 dark:border-indigo-700',
              )}
              title="Sort message list"
              aria-haspopup="menu"
              aria-expanded={sortMenuOpen}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              </svg>
              <span className="truncate">{emailListSortLabel(listSortBy)}</span>
            </button>
            {sortMenuOpen && sortMenuRect && createPortal(
              <div
                ref={sortMenuDropdownRef}
                className={cn('fixed w-60 rounded-lg border shadow-lg p-2', colors.surface, colors.border)}
                style={{ top: sortMenuRect.top, left: sortMenuRect.left, ...zStyle(Z_INDEX.portaledDropdown) }}
                role="menu"
              >
                <p className={cn('px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                  Sort by (newest first)
                </p>
                <div className="space-y-1">
                  {EMAIL_LIST_SORT_OPTIONS.map((option) => {
                    const isActive = listSortBy === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          onListSortByChange(option.value);
                          setSortMenuOpen(false);
                        }}
                        className={cn(
                          'w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                          isActive ? 'bg-indigo-600 text-white' : cn(colors.text, colors.interactiveHover),
                        )}
                        title={option.hint}
                      >
                        <span className="font-medium block">{option.label}</span>
                        <span className={cn('block text-[10px] mt-0.5', isActive ? 'text-indigo-100' : colors.textMuted)}>
                          {option.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>,
              document.body,
            )}
          </div>
        )}
        {showListLayoutToggle && onThreadedViewChange && (
          <EmailListLayoutToggle
            threadedView={threadedView}
            onChange={onThreadedViewChange}
            compact={isCompactFilters}
          />
        )}
        {resultCount !== undefined && (
          <span className={cn('text-[11px] whitespace-nowrap', colors.textMuted)}>
            {resultCount.toLocaleString()} {resultCount === 1 ? 'message' : 'messages'}
            {searchFolderName && (
              <>
                {' · '}
                in <span className="font-semibold">"{searchFolderName}"</span>
                {searchFolderPeriodLabel && (
                  <>
                    {' · '}
                    <span className="font-medium">{searchFolderPeriodLabel}</span>
                  </>
                )}
              </>
            )}
          </span>
        )}
      </div>
      </div>
    </div>
  );
};

export default EmailFilters;
